/**
 * RegistrationSuccessPage.js
 * Dedicated success page shown after a registrant completes Stripe Checkout.
 * Route: /register/:leagueId/success (public, no auth required)
 *
 * Stripe redirects here with ?session_id={CHECKOUT_SESSION_ID} after a successful payment.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Logo from './Logo';
import { getPublicRegistrationForm } from '../services/api';

export default function RegistrationSuccessPage() {
  const { leagueId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [league, setLeague] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch league info for display (best-effort — page works even if this fails)
    getPublicRegistrationForm(leagueId)
      .then(data => {
        setLeague(data.league);
        setForm(data.form);
      })
      .catch(() => {
        // ignore — form may be closed, league may not exist; page still renders
      })
      .finally(() => setLoading(false));
  }, [leagueId]);

  const sportLabel = league?.sport && league.sport !== 'general'
    ? league.sport.charAt(0).toUpperCase() + league.sport.slice(1)
    : null;

  const feeAmount = form?.feeAmount;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Branding bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px',
        background: 'rgba(0,0,0,0.15)',
      }}>
        <a
          href="https://leaguemaker.app"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <Logo variant="white" size="1.1rem" />
        </a>
        <a
          href="https://leaguemaker.app/signup"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#fff',
            textDecoration: 'none',
            fontSize: '0.85rem',
            fontWeight: 600,
            padding: '6px 16px',
            borderRadius: 20,
            background: 'rgba(255,255,255,0.2)',
          }}
        >
          Start a League — Free
        </a>
      </div>

      {/* League header */}
      {!loading && league && (
        <div style={{ textAlign: 'center', padding: '32px 20px 0' }}>
          <h1 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: 700 }}>
            {league.name}
          </h1>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {sportLabel && (
              <span style={{
                background: 'rgba(255,255,255,0.2)', color: '#fff',
                padding: '3px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 600
              }}>
                {sportLabel}
              </span>
            )}
            {league.seasonYear && (
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                Season {league.seasonYear}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Success card */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '32px 16px 48px',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '48px 40px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        }}>
          {/* Checkmark icon */}
          <div style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h2 style={{ margin: '0 0 12px 0', fontSize: '1.75rem', fontWeight: 700, color: '#111' }}>
            Registration Complete!
          </h2>
          <p style={{ margin: '0 0 24px 0', color: '#555', fontSize: '1rem', lineHeight: 1.6 }}>
            Your payment has been received and your registration is confirmed.
            A confirmation email has been sent to you.
          </p>

          {feeAmount && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: 10,
              padding: '14px 20px',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}>
              <span style={{ fontSize: '1.1rem' }}>✓</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '0.95rem' }}>Payment received</div>
                <div style={{ color: '#555', fontSize: '0.85rem' }}>
                  ${parseFloat(feeAmount).toFixed(2)} paid successfully
                </div>
              </div>
            </div>
          )}

          {sessionId && (
            <p style={{ color: '#9ca3af', fontSize: '0.78rem', marginBottom: 24 }}>
              Reference: {sessionId.slice(0, 20)}…
            </p>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {league?.slug && (
              <a
                href={`/public/${league.slug}`}
                style={{
                  display: 'block',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                View League Schedule →
              </a>
            )}
            <a
              href="https://leaguemaker.app"
              style={{
                display: 'block',
                padding: '11px 24px',
                border: '1px solid #e5e7eb',
                color: '#667eea',
                textDecoration: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              LeagueMaker Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
